import iconCalendarRangeLight from '@fortawesome/fontawesome-pro/svgs/light/calendar-range.svg?raw';
import iconCalendarRangeRegular from '@fortawesome/fontawesome-pro/svgs/regular/calendar-range.svg?raw';
import iconLocationDotRegular from '@fortawesome/fontawesome-pro/svgs/regular/location-dot.svg?raw';
import iconCircleCheckRegular from '@fortawesome/fontawesome-pro/svgs/regular/circle-check.svg?raw';
import iconCircleExclamationRegular from '@fortawesome/fontawesome-pro/svgs/regular/circle-exclamation.svg?raw';
import iconCircleXmarkRegular from '@fortawesome/fontawesome-pro/svgs/regular/circle-xmark.svg?raw';
import iconPodcastLight from '@fortawesome/fontawesome-pro/svgs/light/podcast.svg?raw';
import iconMicrophoneRegular from '@fortawesome/fontawesome-pro/svgs/regular/microphone.svg?raw';
import iconCalendarWeekRegular from '@fortawesome/fontawesome-pro/svgs/regular/calendar-week.svg?raw';
import iconHashtagRegular from '@fortawesome/fontawesome-pro/svgs/regular/hashtag.svg?raw';
import iconBuildingRegular from '@fortawesome/fontawesome-pro/svgs/regular/building.svg?raw';
import iconUserRegular from '@fortawesome/fontawesome-pro/svgs/regular/user.svg?raw';
import iconCircleEuroRegular from '@fortawesome/fontawesome-pro/svgs/regular/circle-euro.svg?raw';
import iconNewspaperLight from '@fortawesome/fontawesome-pro/svgs/light/newspaper.svg?raw';
import iconScrewdriverWrenchLight from '@fortawesome/fontawesome-pro/svgs/light/screwdriver-wrench.svg?raw';
import iconGraduationCapLight from '@fortawesome/fontawesome-pro/svgs/light/graduation-cap.svg?raw';
import iconFileLinesLight from '@fortawesome/fontawesome-pro/svgs/light/file-lines.svg?raw';
import iconChalkboardUserRegular from '@fortawesome/fontawesome-pro/svgs/solid/chalkboard-user.svg?raw';
import iconFolderTreeRegular from '@fortawesome/fontawesome-pro/svgs/regular/folder-tree.svg?raw';

const formatDateRange = (startTimestamp, endTimestamp = false) => {
    const format = (timestamp, options) =>
        new Date(timestamp * 1000).toLocaleDateString('de-DE', options);

    if (!endTimestamp) {
        return format(startTimestamp, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    const isSameYear =
        new Date(startTimestamp * 1000).getFullYear() ===
        new Date(endTimestamp * 1000).getFullYear();

    const baseOptions = { day: '2-digit', month: '2-digit' };
    const fullOptions = isSameYear ? baseOptions : { ...baseOptions, year: 'numeric' };

    return `${format(startTimestamp, fullOptions)} – ${format(endTimestamp, {
        ...fullOptions,
        year: 'numeric',
    })}`;
};

const availabilityIcon = (key, html) => {
    switch (key) {
        case 'available':
            return html([iconCircleCheckRegular]);
        case 'nearly-booked':
            return html([iconCircleExclamationRegular]);
        case 'fully-booked':
            return html([iconCircleXmarkRegular]);
        default:
            return '';
    }
};

const isFreeOfCharge = price => ['0,00€', '0.00€'].includes(price?.replace(/\s/g, ''));

const metaItem = (title, icon, text, html) => html`
    <div title="${title}">
        <dt class="visually-hidden">${title}</dt>
        <dd>
            <span class="icon-text">
                <span class="icon-text__icon" aria-hidden="true">${icon}</span>
                <span class="icon-text__text">${text}</span>
            </span>
        </dd>
    </div>
`;

const categoryBadges = (items, html, label = 'Kategorien') =>
    items?.length
        ? html`
              <ul class="search-result__categories badges" aria-label="${label}">
                  ${items.map(
                      item => html`
                          <li
                              class="badges__item${item.color_scheme?.key
                                  ? ` badges__item--${item.color_scheme.key}`
                                  : ''}"
                          >
                              ${item.title}
                          </li>
                      `,
                  )}
              </ul>
          `
        : '';

const imagePreview = (images, html) => {
    if (!images || !images.length) {
        return '';
    }

    const image = images[0];
    const srcset = image.thumbnails
        .map(thumbnail => `${thumbnail.url} ${thumbnail.width}w`)
        .join(', ');
    return html`
        <div class="search-result__preview-image">
            <img
                src="${image.url}"
                alt="${image.alt || ''}"
                srcset="${srcset}"
                sizes="200px"
                width="200"
                height="200"
                loading="lazy"
            />
            ${image.ai_generated
                ? html`<small class="image-credit-badge image-credit-badge--bottom-right">
                      KI-generiert
                  </small>`
                : ''}
        </div>
    `;
};

const entryTypeIcon = (icon, html) =>
    html`<div class="search-result__entry-type-icon" aria-hidden="true">${html([icon])}</div>`;

const headline = (text, url, html, subline = '') => html`
    <div class="search-result__header">
        <p class="search-result__headline">
            <a href="${url}" class="search-result__link">${text}</a>
        </p>
        ${subline ? html`<p class="search-result__subline">${subline}</p>` : ''}
    </div>
`;

const searchResult = (item, html, content) => {
    const classes = ['search-result'];
    if (item.highlight) classes.push('search-result--highlighted');
    if (item.type_handle) classes.push(`search-result--type-${item.type_handle}`);
    return html`<article class="${classes.join(' ')}">${content}</article>`;
};

const episodes = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconPodcastLight, html)}
            ${categoryBadges(
                item.episode_category?.map(category => ({ title: category })) ?? [],
                html,
            )}
        </div>

        ${headline(item.title, item.url, html, item.guest ? `Mit ${item.guest}` : '')}

        <dl class="search-result__meta-list">
            ${item.podcast &&
            metaItem('Podcast', html([iconMicrophoneRegular]), item.podcast, html)}
            ${item.postDate &&
            metaItem(
                'Veröffentlichungsdatum',
                html([iconCalendarWeekRegular]),
                formatDateRange(item.postDate),
                html,
            )}
            ${item.episode &&
            metaItem('Episode', html([iconHashtagRegular]), `Folge ${item.episode}`, html)}
        </dl>

        ${imagePreview(item.cover ? item.cover : [], html)}
    `;

    return searchResult(item, html, content);
};

const projects = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconScrewdriverWrenchLight, html)}
        </div>

        ${headline(item.title, item.url, html, item.description_short)}

        <dl class="search-result__meta-list">
            ${item.project_date_start &&
            metaItem(
                'Projektzeitraum',
                html([iconCalendarRangeRegular]),
                formatDateRange(item.project_date_start, item.date_end),
                html,
            )}
        </dl>

        ${imagePreview(item.image ? item.image : [], html)}
    `;

    return searchResult(item, html, content);
};

const publications = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconNewspaperLight, html)}
            ${categoryBadges(
                item.publications_category?.map(category => ({ title: category })) ?? [],
                html,
                'Kategorien',
            )}
        </div>

        ${headline(item.title, item.url, html)}

        <dl class="search-result__meta-list">
            ${item.publication_date &&
            metaItem(
                'Veröffentlichungsdatum',
                html([iconCalendarWeekRegular]),
                formatDateRange(item.publication_date),
                html,
            )}
            ${item.publisher &&
            metaItem('Verlag', html([iconBuildingRegular]), item.publisher, html)}
            ${item.writers &&
            metaItem(
                'Autor(en)',
                html([iconUserRegular]),
                Array.isArray(item.writers) ? item.writers.join(', ') : item.writers,
                html,
            )}
            ${item.publication_price &&
            metaItem('Preis', html([iconCircleEuroRegular]), item.publication_price, html)}
        </dl>

        ${imagePreview(item.image ? item.image : [], html)}
    `;

    return searchResult(item, html, content);
};

const events = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconCalendarRangeLight, html)}
            ${categoryBadges(item.topics, html, 'Themenwelten')}
        </div>

        ${headline(item.title, item.url, html, item.tagline || item.product?.[0]?.tagline)}

        <dl class="search-result__meta-list">
            ${item.date_start &&
            metaItem(
                'Veranstaltungsdatum',
                html([iconCalendarRangeRegular]),
                formatDateRange(item.date_start, item.date_end),
                html,
            )}
            ${item.availability &&
            metaItem(
                'Verfügbarkeit',
                availabilityIcon(item.availability.key, html),
                item.availability.label,
                html,
            )}
            ${item.location.length && item.location.some(l => l.address)
                ? metaItem(
                      'Veranstaltungsort',
                      html([iconLocationDotRegular]),
                      item.location
                          .filter(l => l.address)
                          .map(
                              l =>
                                  l.address.locality ||
                                  l.address.organization ||
                                  l.address.addressLine1,
                          )
                          .join(', '),
                      html,
                  )
                : ''}
            ${isFreeOfCharge(item.price)
                ? metaItem('Preis', html([iconCircleEuroRegular]), 'Kostenfrei', html)
                : item.member_price
                  ? metaItem(
                        'Preis',
                        html([iconCircleEuroRegular]),
                        `Ab ${item.member_price}`,
                        html,
                    )
                  : item.price
                    ? metaItem('Preis', html([iconCircleEuroRegular]), item.price, html)
                    : ''}
        </dl>

        ${imagePreview(item.product?.[0]?.image, html)}
    `;

    return searchResult(item, html, content);
};

const digitalProducts = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconGraduationCapLight, html)}
            ${categoryBadges(item.topics, html, 'Themenwelten')}
        </div>

        ${headline(item.product?.[0]?.title || item.title, item.url, html, item.tagline)}

        <dl class="search-result__meta-list">
            ${item.digital_format.length
                ? metaItem('Format', html([iconChalkboardUserRegular]), item.digital_format, html)
                : ''}
            ${item.price &&
            metaItem(
                'Preis',
                html([iconCircleEuroRegular]),
                isFreeOfCharge(item.price) ? 'Kostenfrei' : item.price,
                html,
            )}
        </dl>
        ${imagePreview(item.image ? item.image : [], html)}
    `;

    return searchResult(item, html, content);
};

const products = (item, { html }) => {
    const content = html`
        <div class="search-result__category-header">
            ${entryTypeIcon(iconGraduationCapLight, html)}
            ${categoryBadges(item.topics, html, 'Themenwelten')}
        </div>

        ${headline(item.title, item.url, html, item.tagline)}

        <dl class="search-result__meta-list">
            ${item.product_type &&
            metaItem('Produktart', html([iconFolderTreeRegular]), item.product_type, html)}
            ${item.duration &&
            metaItem('Dauer', html([iconCalendarWeekRegular]), item.duration, html)}
        </dl>
        ${imagePreview(item.image ? item.image : [], html)}
    `;

    return searchResult(item, html, content);
};

const pages = (item, { html }) => {
    const heroTextPreview = item.hero_text?.substring(0, 200) || '';
    const heroTextWithEllipsis =
        heroTextPreview && item.hero_text?.length > 200 ? `${heroTextPreview}…` : heroTextPreview;

    const content = html`
        <div class="search-result__category-header">${entryTypeIcon(iconFileLinesLight, html)}</div>

        ${headline(item.title, item.url, html, heroTextWithEllipsis)}
        ${imagePreview(
            item.seo_image?.length
                ? item.seo_image
                : item.hero_image?.length
                  ? item.hero_image
                  : [],
            html,
        )}
    `;

    return searchResult(item, html, content);
};

export const itemTemplatesBySection = {
    digital_products: digitalProducts,
    episodes: episodes,
    events: events,
    products: products,
    projects: projects,
    publications: publications,
    pages: pages,
};
